import { Box, Skeleton, Typography } from '@mui/material';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DraggableProvided,
  DraggableStateSnapshot,
  DropResult,
} from '@hello-pangea/dnd';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import { TileActionButton } from './PageReorderGrid';

export interface CoverPagesRowProps {
  /**
   * Ordered list of original PDF page numbers designated as cover pages.
   * Array order is the prepend order — do not sort.
   */
  coverPages: number[];
  onReorder: (next: number[]) => void;
  /** Page image URL keyed by ORIGINAL PDF page number (1-indexed). */
  pageImages: Record<number, string | undefined>;
  imagesLoading?: boolean;
  rotations?: Record<number, number>;
  onRotate?: (pageNumber: number, delta: -90 | 90) => void;
  onView?: (pageNumber: number) => void;
  /** Toggle the page out of the cover stack (back into the main grid). */
  onUnmark: (pageNumber: number) => void;
}

function reorder<T>(list: T[], from: number, to: number): T[] {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

/**
 * Horizontal row of cover-page tiles, anchored above the main reorder grid.
 * Visible only when at least one page is designated as a cover. Cover order is
 * the prepend order: each child document materializes with the cover stack
 * prefixed in this order. Tiles are drag-reorderable within the row. No
 * boundary or omit affordances — covers are prepended to every child and
 * cover/omit are mutually exclusive (gp-025#15).
 *
 * Ported from glynt-ui-next's CoverPagesRow: native drag-drop →
 * @hello-pangea/dnd, shadcn/Tailwind → MUI, per-page image fetch → image URLs
 * supplied by the parent.
 */
export function CoverPagesRow({
  coverPages,
  onReorder,
  pageImages,
  imagesLoading = false,
  rotations,
  onRotate,
  onView,
  onUnmark,
}: CoverPagesRowProps) {
  function handleDragEnd(result: DropResult) {
    if (!result.destination) return;
    if (result.source.index === result.destination.index) return;
    onReorder(reorder(coverPages, result.source.index, result.destination.index));
  }

  if (coverPages.length === 0) return null;

  return (
    <Box
      component="section"
      data-cy="cover-pages-row"
      aria-label="Cover pages"
      sx={{
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'primary.light',
        backgroundColor: (t) => `${t.palette.primary.main}0D`, // ~5% tint
        p: 1.5,
      }}
    >
      <Box
        sx={{
          mb: 1,
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Typography
          component="h2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.75,
            fontSize: 14,
            fontWeight: 600,
            color: 'primary.main',
          }}
        >
          <StarIcon sx={{ fontSize: 16 }} />
          Cover pages
          <Typography component="span" sx={{ fontSize: 12, color: 'text.secondary' }}>
            (prepended to every child, in this order)
          </Typography>
        </Typography>
        <Typography
          component="span"
          data-cy="cover-pages-count"
          sx={{ fontSize: 12, color: 'text.secondary' }}
        >
          {coverPages.length} page{coverPages.length === 1 ? '' : 's'}
        </Typography>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="cover-pages-row" direction="horizontal">
          {(provided) => (
            <Box
              ref={provided.innerRef}
              {...provided.droppableProps}
              data-cy="cover-pages-tiles"
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}
            >
              {coverPages.map((pageNumber, idx) => (
                <Draggable
                  key={pageNumber}
                  draggableId={`cover-${pageNumber}`}
                  index={idx}
                >
                  {(dp: DraggableProvided, snap: DraggableStateSnapshot) => (
                    <CoverTile
                      provided={dp}
                      isDragging={snap.isDragging}
                      pageNumber={pageNumber}
                      coverIndex={idx}
                      imageUrl={pageImages[pageNumber]}
                      imagesLoading={imagesLoading}
                      rotation={rotations?.[pageNumber] ?? 0}
                      onRotate={onRotate ? (delta) => onRotate(pageNumber, delta) : undefined}
                      onView={onView ? () => onView(pageNumber) : undefined}
                      onUnmark={() => onUnmark(pageNumber)}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>
    </Box>
  );
}

interface CoverTileProps {
  provided: DraggableProvided;
  isDragging: boolean;
  pageNumber: number;
  coverIndex: number;
  imageUrl?: string;
  imagesLoading: boolean;
  rotation: number;
  onRotate?: (delta: -90 | 90) => void;
  onView?: () => void;
  onUnmark: () => void;
}

function CoverTile({
  provided,
  isDragging,
  pageNumber,
  coverIndex,
  imageUrl,
  imagesLoading,
  rotation,
  onRotate,
  onView,
  onUnmark,
}: CoverTileProps) {
  const isQuarter = rotation === 90 || rotation === 270;
  const transform = rotation
    ? `rotate(${rotation}deg)${isQuarter ? ' scale(0.72)' : ''}`
    : undefined;
  const stopDrag = (e: React.MouseEvent | React.DragEvent) => e.stopPropagation();
  const id = coverIndex + 1;

  return (
    <Box
      ref={provided.innerRef}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      data-cy={`cover-tile-${id}`}
      data-page-number={pageNumber}
      data-rotation={rotation || undefined}
      sx={{
        position: 'relative',
        width: '10rem',
        cursor: 'grab',
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'primary.light',
        bgcolor: 'background.paper',
        p: 1,
        transition: 'all 120ms ease',
        opacity: isDragging ? 0.5 : 1,
        '&:active': { cursor: 'grabbing' },
        '& .tile-hover': { opacity: 0, transition: 'opacity 120ms ease' },
        '&:hover .tile-hover': { opacity: 1 },
        ...(provided.draggableProps.style as object),
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 8,
          top: 8,
          zIndex: 2,
          borderRadius: 0.5,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          px: 0.75,
          py: 0.25,
          fontSize: 12,
          fontWeight: 600,
          lineHeight: 1,
          boxShadow: 1,
        }}
      >
        Cover {id}
      </Box>
      <Box
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 0.5,
        }}
      >
        <Box
          sx={{
            borderRadius: 0.5,
            bgcolor: 'rgba(255,255,255,0.9)',
            px: 0.75,
            py: 0.25,
            fontSize: 12,
            lineHeight: 1,
            color: 'text.secondary',
            boxShadow: 1,
          }}
        >
          Page {pageNumber}
        </Box>
        {rotation ? (
          <Box
            data-cy={`cover-tile-${id}-rotation-badge`}
            aria-label={`Rotated ${rotation} degrees`}
            sx={{
              borderRadius: 0.5,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              px: 0.75,
              py: 0.25,
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1,
              boxShadow: 1,
            }}
          >
            {rotation}°
          </Box>
        ) : null}
      </Box>

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          aspectRatio: '3 / 4',
          overflow: 'hidden',
          borderRadius: 1,
        }}
      >
        {imagesLoading ? (
          <Skeleton variant="rectangular" width="100%" height="100%" />
        ) : !imageUrl ? (
          <Typography variant="caption" color="text.secondary">
            No preview
          </Typography>
        ) : (
          <Box
            component="img"
            src={imageUrl}
            alt={`Cover ${id} — page ${pageNumber}`}
            draggable={false}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform,
              transformOrigin: 'center center',
              transition: 'transform 150ms ease',
            }}
          />
        )}

        <Box
          className="tile-hover"
          sx={{
            position: 'absolute',
            insetInline: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            gap: 0.5,
            p: 0.5,
          }}
        >
          {onView ? (
            <TileActionButton
              cy={`cover-tile-${id}-view`}
              label="View page"
              onClick={onView}
              stopDrag={stopDrag}
            >
              <VisibilityIcon sx={{ fontSize: 16 }} />
            </TileActionButton>
          ) : null}
          {onRotate ? (
            <>
              <TileActionButton
                cy={`cover-tile-${id}-rotate-left`}
                label="Rotate page left"
                onClick={() => onRotate(-90)}
                stopDrag={stopDrag}
              >
                <RotateLeftIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
              <TileActionButton
                cy={`cover-tile-${id}-rotate-right`}
                label="Rotate page right"
                onClick={() => onRotate(90)}
                stopDrag={stopDrag}
              >
                <RotateRightIcon sx={{ fontSize: 16 }} />
              </TileActionButton>
            </>
          ) : null}
          <TileActionButton
            cy={`cover-tile-${id}-unmark`}
            label="Remove from cover pages"
            onClick={onUnmark}
            stopDrag={stopDrag}
          >
            <StarBorderIcon sx={{ fontSize: 16 }} />
          </TileActionButton>
        </Box>
      </Box>
    </Box>
  );
}
